import React from "react"
import useCustomStyles from "@/styles/custom"
import {
	TableHead,
	Table,
	TableContainer,
	TableRow,
	TableCell,
	TableBody,
	Grid,
	Typography
} from "@material-ui/core"
import { Skeleton } from "@material-ui/lab"

export type AlignOptions = "right" | "left" | "center"

const MIN_SKELETON_ROWS = 6

export type TableColumn<RowData> = {
	field: keyof RowData
	label: string
	align?: AlignOptions
	width?: number
	headRender?: (value: string) => React.ReactNode
	rowRender?: (value: RowData[keyof RowData]) => React.ReactNode
}

type DataTableProps<RowData> = {
	columns: TableColumn<RowData>[]
	data: RowData[]
	loading?: boolean
	skeletonRows?: number
	getRowProps?: (row: RowData, index: number) => React.HTMLAttributes<HTMLTableRowElement>
}

const DataTable = <RowData, >(props: DataTableProps<RowData>) => {
	const {
		columns,
		data,
		loading = false,
		skeletonRows = MIN_SKELETON_ROWS,
		getRowProps
	} = props

	const customClasses = useCustomStyles()

	return (
		<>
			<TableContainer>
				<Table stickyHeader size="small">
					<TableHead>
						<TableRow>
							{columns.map((column) => (
								<TableCell
									key={String(column.field)}
									align={column.align || "left"}
									style={{ width: `${column.width}%` || "auto" }}
								>
									{column.headRender ? column.headRender(column.label) : column.label}
								</TableCell>
							))}
						</TableRow>
					</TableHead>

					<TableBody className={customClasses.reportTableBodyText}>
						{loading &&
							Array.from({ length: skeletonRows }).map((_, rowIndex) => (
								<TableRow key={`skeleton-${rowIndex}`}>
									{columns.map(column => (
										<TableCell key={String(column.field)}>
											<Skeleton height={40} />
										</TableCell>
									))}
								</TableRow>
							))}

						{!loading && data?.length > 0 &&
							data.map((row, rowIndex) => (
								<TableRow key={rowIndex} {...(getRowProps?.(row, rowIndex) ?? {})}>
									{columns.map(column => (
										<TableCell
											key={String(column.field)}
											align={column.align || "left"}
											style={{ verticalAlign: "middle" }}
										>
											{column.rowRender
												? column.rowRender(row[column.field])
												: row[column.field] ?? "-"}
										</TableCell>
									))}
								</TableRow>
							))}
					</TableBody>
				</Table>

				{!loading && !data?.length && (
					<Grid item xs={12} style={{ margin: "24px" }}>
						<Typography
							align="center"
							variant="h2">
							Nenhum resultado encontrado
						</Typography>
					</Grid>
				)}
			</TableContainer>
		</>
	)
}

export default DataTable
